import { useState } from 'react';
import { Card, SegmentedControl } from '@salonomy/ui';
import { MoneyAdminTab, ReportsTab } from '../features/reports/index.js';
import { useAuth } from '../lib/auth.js';

/**
 * WS9 — the reports + money-admin surface, rewritten into a tabbed shell. The old MIS-SOURCED client-side
 * projection (a useQuery over /v1/appointments summed in JS) and the client-side per-chair cards are GONE —
 * they are replaced by the SERVER's future-revenue projection and staff-KPI reports in the Reports tab
 * (money honesty, ADR-0009: no figure is recomputed in the browser).
 *
 * Self-gated on isManager (owner/manager) with an honest note for non-managers — the manager-only report
 * endpoints (comms-cost, payment-cost-composition) and every admin mutation live behind this gate, so a
 * non-manager gets a clear explanation rather than a guaranteed 403. The two tabs are built in parallel
 * (ReportsTab / MoneyAdminTab); this shell just routes between them.
 */
type Tab = 'reports' | 'money';

const TABS = [
  { value: 'reports' as const, label: 'Reports' },
  { value: 'money' as const, label: 'Money admin' },
];

export function ReportsPage() {
  const { isManager } = useAuth();
  const [tab, setTab] = useState<Tab>('reports');

  return (
    <>
      <div className="topbar">
        <div className="topbar__left">
          <div className="topbar__title">Reports</div>
          <span className="muted">Revenue, takings &amp; money admin</span>
        </div>
      </div>

      <div className="screen-scroll">
        <div
          className="screen-pad"
          style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 980 }}
        >
          {!isManager ? (
            <Card>
              <div className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
                Reports and money admin are available to salon owners and managers. Ask an owner or
                manager for the figures you need — your own earnings and KPIs appear on your
                profile.
              </div>
            </Card>
          ) : (
            <>
              <SegmentedControl options={TABS} value={tab} onChange={setTab} />
              {tab === 'reports' ? <ReportsTab /> : <MoneyAdminTab />}
            </>
          )}
        </div>
      </div>
    </>
  );
}
